import { produce } from 'immer';

import { createCommands } from "@tdp/common/commandHandler";
import { ApiFetchStatus } from "@tdp/common/constants";
import downloadFile from "@tdp/common/downloadUtils/downloadFile";
import { LongRunningOperation, lroSlice } from "@tdp/common/lro";
import { notificationSlice, NotificationType } from '@tdp/common/notification';
import { ILogger } from "@tdp/common/telemetry";

import { appListSlice } from "./slice";
import { IAppListItemDelete } from "../abstractions/IAppListItemDelete";
import { IAppListItemWriter } from "../abstractions/IAppListItemWriter";
import { IAppListLro } from "../abstractions/IAppListLro";
import { IAppListReader } from "../abstractions/IAppListReader";

export interface PackageDownloadLro {
  appPackageDownloaded: boolean
}
export interface IAppListCommandHandler {
  logger: ILogger;
  appListReader: IAppListReader;
  appListItemDelete: IAppListItemDelete;
  appListItemWriter: IAppListItemWriter;
}

export interface IDuplicateAppPayload {
  appId: string;
  shortName: string;
  onSuccess: () => void;
  onFailure: () => void;
}

export const createAppListCommandHandler = ({ logger, appListReader, appListItemDelete, appListItemWriter }: IAppListCommandHandler) =>
  createCommands(
    {
      name: "appListCommands",
      handlers: {

        async *loadApps(n = 1) {
          const appsListResult = await appListReader.readAppList(n);
          if (appsListResult.type === ApiFetchStatus.ERROR) {
            logger.error("Error loading apps list | reason: %s", appsListResult.errorType);
            logger.logUserBi({ telemetryMarker: 'api-action-apps-load-apps-failed', message: `User failed to load apps list with page number: ${n}` });
            yield notificationSlice.actions.showNotification({
              message: `Failed to load apps list`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            yield appListSlice.actions.appListLoadingFailed();
            return;
          }
          yield appListSlice.actions.appsListLoaded(appsListResult.appsList);
        },

        async *getAppsCount() {
          const count = await appListReader.readAppsCount()
          yield appListSlice.actions.appCountLoaded(count)
        },

        async *deleteApp(appId: string) {
          const response = await appListItemDelete.deleteApp(appId);
          if (response.type === ApiFetchStatus.ERROR) {
            yield notificationSlice.actions.showNotification({
              message: `Failed to delete app`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
          }
          else {
            yield notificationSlice.actions.showNotification({
              message: `App deleted successfully`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Success,
            });
            yield appListSlice.actions.appListItemDeleted(appId);
          }
        },

        async *createApp({ lro, payload }: {
          lro: LongRunningOperation<IAppListLro>;
          payload: object;
        }) {
          yield lroSlice.actions.lroCreated(lro);
          const response = await appListItemWriter.createApp(payload);
          if (response.type === ApiFetchStatus.ERROR) {
            yield notificationSlice.actions.showNotification({
              message: `Failed to create app`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            logger.logUserBi({ telemetryMarker: 'api-action-apps-create-new-app-failed', message: `User failed to create a new app with manifest version: ${payload.manifestVersion}` });

          }
          else {
            const summary = await appListItemWriter.writeAppSummary({
              teamsAppId: response.response.teamsAppId,
              appId: response.response.teamsAppId,
              ...payload
            });
            if (summary.type === ApiFetchStatus.ERROR) {
              yield notificationSlice.actions.showNotification({
                message: `Failed to write app summary`,
                show: true,
                autoDismiss: true,
                type: NotificationType.Error,
              });
              logger.logUserBi({ telemetryMarker: 'api-action-apps-create-new-app-failed', message: `User failed to write app summary for new app with manifest version: ${payload.manifestVersion}` });

              return;
            }
            logger.logUserBi({ telemetryMarker: 'api-action-apps-create-new-app-successful', message: `User created a new app with manifest version: ${payload.manifestVersion}` });
            yield notificationSlice.actions.showNotification({
              message: `App created successfully`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Success,
            });
            yield lroSlice.actions.lroUpdated(
              produce(lro, (nextLro) => {
                nextLro.state.newAppId = response.response.teamsAppId;
                nextLro.state.isAppItemUpdated = true;
              })
            );
            yield appListSlice.actions.newAppCreatedSuccess(response.response);
            return;
          }
        },

        async *duplicateApp(payload: IDuplicateAppPayload) {
          const { appId, shortName, onSuccess, onFailure } = payload;
          const response = await appListItemWriter.duplicateApp(appId, shortName);
          if (response.type === ApiFetchStatus.ERROR) {
            yield notificationSlice.actions.showNotification({
              message: `Failed to duplicate app`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            onFailure();
          }
          else {
            yield notificationSlice.actions.showNotification({
              message: `App duplicated successfully`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Success,
            });
            yield appListSlice.actions.newAppCreatedSuccess(response.response);
            onSuccess();
          }
        },

        async *downloadApp(appId: string) {
          const result = await appListReader.downloadApp(appId);
          if (result.type === ApiFetchStatus.ERROR) {
            logger.logUserBi({ telemetryMarker: 'api-action-apps-download-app-failed', message: `User failed to download app with id: ${appId}` });
            yield notificationSlice.actions.showNotification({
              message: `Failed to download app`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            return;
          }
          else {
            logger.logUserBi({ telemetryMarker: 'api-action-apps-download-app-successful', message: `User downloaded app with id: ${appId}` });
            downloadFile(appId, result.appPackage);
          }
        },

        async *importApp({ lro, file, overwriteIfAppAlreadyExists }:
          {
            lro: LongRunningOperation<IAppListLro>,
            file: File,
            overwriteIfAppAlreadyExists: boolean
          }) {
          yield lroSlice.actions.lroCreated(lro);
          const response = await appListItemWriter.importApp(overwriteIfAppAlreadyExists, file)
          if (response.type === ApiFetchStatus.SUCCESS) {
            yield appListSlice.actions.newAppCreatedSuccess(response.response);
            yield lroSlice.actions.lroUpdated(
              produce(lro, (nextLro) => {
                nextLro.state.newAppId = response.response.teamsAppId;
                nextLro.state.isAppItemUpdated = true;
              })
            );
            logger.logUserBi({ telemetryMarker: 'api-action-apps-import-app-successful', message: `User imported an app with manifest version: ${response.response.manifestVersion}` });
            yield notificationSlice.actions.showNotification({
              message: `App imported successfully`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Success,
            });
          }
          else {
            logger.logUserBi({ telemetryMarker: 'api-action-apps-import-app-failed', message: `User failed to import an app` });

            yield notificationSlice.actions.showNotification({
              message: `Failed to import app`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
          }
          yield lroSlice.actions.lroCompleted(
            produce(lro, (nextLro) => {
              nextLro.state.isAppItemUpdated = true;
              nextLro.state.newAppId = '';
            })
          );
        },

        async *takeOwnership({
          appId,
          onCallback,
          onFailure,
        }: { appId: string; onCallback: () => void; onFailure: () => void }) {
          const response = await appListItemWriter.takeOwnership(appId);
          if (response.type === ApiFetchStatus.SUCCESS) {
            onCallback();
            logger.logUserBi({ telemetryMarker: 'api-action-apps-take-ownership-successful', message: `User took ownership of app with id: ${appId}` });
            yield notificationSlice.actions.showNotification({
              message: `App ownership taken successfully`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Success,
            });
          }
          else {
            onFailure();
            logger.logUserBi({ telemetryMarker: 'api-action-apps-take-ownership-failed', message: `User failed to take ownership of app with id: ${appId}` });
            yield notificationSlice.actions.showNotification({
              message: `Failed to take ownership`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
          }
        },

        async *firstPartyPublishingStatus({ appId, onCallback, onFailure }:
          { appId: string; onCallback: (message: string) => void; onFailure: (message: string) => void }) {
          const response = await appListReader.firstPartyPublish(appId);
          if (response.type === ApiFetchStatus.SUCCESS) {
            onCallback(response.appPackage);
            return;
          }
          else {
            onFailure(response.error ?? "Error getting publishing status");
            return;
          }
        },

        async *firstPartyImportCatalog({
          payload,
          onCallback,
          onFailure,
        }: { payload: Record<string, string>; onCallback: () => void; onFailure: () => void }) {
          const response = await appListItemWriter.firstPartyImportCatalog(payload);
          if (response.type === ApiFetchStatus.SUCCESS) {
            onCallback();
            yield notificationSlice.actions.showNotification({
              message: `App imported successfully`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Success,
            });
          }
          else {
            onFailure();
            yield notificationSlice.actions.showNotification({
              message: `Failed to import app`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
          }
          return;
        }
      },
    });
